import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from "@angular/core";
import { ExtendedWarrantyService } from "../../services/extended-warranty.service";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Factor } from "../../model/factor";
import { Entity } from "../../entity/entity";
import { ExtendedWarrantyUtils } from "../../utils/extended-warranty-utils";
import { StringUtils } from "../../utils/string-utils";
import { MatIcon } from "@angular/material/icon";
import { MatButton } from "@angular/material/button";
import { FormsModule } from "@angular/forms";
import { CurrencyPipe } from "@angular/common";
import { EntityBreak } from "../../entity/entity-break";
import { MatRadioButton, MatRadioGroup } from "@angular/material/radio";
import { HttpErrorResponse } from "@angular/common/http";
import { firstValueFrom } from "rxjs";

@Component({
  selector: "app-extended-warranty-dialog",
  templateUrl: "./extended-warranty-dialog.component.html",
  styleUrls: ["./extended-warranty-dialog.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatButton, MatIcon, CurrencyPipe, MatRadioGroup, MatRadioButton],
})
export class ExtendedWarrantyDialogComponent implements OnInit {
  private readonly extendedWarrantyService = inject(ExtendedWarrantyService);
  private readonly dialogRef = inject<MatDialogRef<ExtendedWarrantyDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);
  private readonly entity: Entity;
  private entityBasePrice = 0;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  warningMessage!: string;
  estimatedPrice!: number;
  availableBoardWarrantyCodes: Factor[] = [];
  availableRefrigerationWarrantyCodes: Factor[] = [];
  allFactors: Factor[] = [];
  refrigerationRadioValue!: string;
  boardRadioValue!: string;

  constructor() {
    this.entity = JSON.parse(JSON.stringify(this.data.entity));
    if (!ExtendedWarrantyUtils.canWarrantyBeChanged(this.entity)) {
      this.warningMessage = "You cannot change the extended warranty code for this entity";
    }
  }

  ngOnInit(): void {
    this.isBusy.set(true);
    this.initialize().then(
      () => (this.isBusy.set(false)),
      (error: HttpErrorResponse) => this.handleError(error),
    );
  }

  private async initialize(): Promise<void> {
    const entityId: string = this.entity.id.entityId;
    const revision: number = this.entity.id.entityRevision;
    this.allFactors = await firstValueFrom<Factor[]>(this.extendedWarrantyService.getExtendedWarrantyFactors());
    this.availableRefrigerationWarrantyCodes = this.allFactors
      .sort((first, second) => first.partsWarrantyMonths - second.partsWarrantyMonths)
      .filter((factor) => this.isRefrigerationWarrantyCode(factor.code));
    this.availableBoardWarrantyCodes = this.allFactors
      .sort((first, second) => first.partsWarrantyMonths - second.partsWarrantyMonths)
      .filter((factor) => this.isBoardWarrantyCode(factor.code));
    const entityBreaks = await firstValueFrom<EntityBreak[]>(this.extendedWarrantyService.getCurrentExtendedWarrantyBreaks(entityId, revision, this.entity.brandID));
    for (const currentBreak of entityBreaks) {
      if (this.isBoardWarrantyCode(currentBreak.factorCode)) {
        this.boardRadioValue = currentBreak.factorCode;
      }
      if (this.isRefrigerationWarrantyCode(currentBreak.factorCode)) {
        this.refrigerationRadioValue = currentBreak.factorCode;
      }
    }
    const basePrice: string = await firstValueFrom<string>(this.extendedWarrantyService.getEntityBasePrice(entityId, revision));
    this.entityBasePrice = +basePrice;
    this.updatePricing();
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    this.isBusy.set(true);
    this.updateExtendedWarranty().then(() => {
      this.isBusy.set(false);
      this.dialogRef.close(true);
    }, (error: HttpErrorResponse) => this.handleError(error));
  }

  private async updateExtendedWarranty(): Promise<void> {
    const warrantyCodes: string[] = [];
    if (this.refrigerationRadioValue && this.refrigerationRadioValue.length > 0) {
      warrantyCodes.push(this.refrigerationRadioValue);
    }
    if (this.boardRadioValue && this.boardRadioValue.length > 0) {
      warrantyCodes.push(this.boardRadioValue);
    }
    const entityId: string = this.entity.id.entityId;
    const revision: number = this.entity.id.entityRevision;
    await firstValueFrom<string>(this.extendedWarrantyService.removeExistingExtendedWarrantyBreaks(entityId, revision, this.entity.brandID));
    await firstValueFrom<string>(this.extendedWarrantyService.addExtendedWarrantyBreaks(entityId, revision, warrantyCodes));
  }

  changesDisabled(): boolean {
    return !!(this.isBusy() || (this.warningMessage && this.warningMessage.length > 0));
  }

  updatePricing() {
    this.estimatedPrice = 0;
    if (this.refrigerationRadioValue && this.refrigerationRadioValue.length > 0 && this.findFactorByCode(this.refrigerationRadioValue)) {
      const factorValue = this.findFactorByCode(this.refrigerationRadioValue)!.factorValue;
      this.estimatedPrice = this.estimatedPrice + this.entityBasePrice * factorValue;
    }
    if (this.boardRadioValue && this.boardRadioValue.length > 0 && this.findFactorByCode(this.boardRadioValue)) {
      const factorValue = this.findFactorByCode(this.boardRadioValue)!.factorValue;
      this.estimatedPrice = this.estimatedPrice + this.entityBasePrice * factorValue;
    }
  }

  private findFactorByCode(code: string): Factor | undefined {
    return this.allFactors.find((x) => x.code === code);
  }

  private isRefrigerationWarrantyCode(code: string): boolean {
    return code === "EWPC" || code === "EWPR";
  }

  private isBoardWarrantyCode(code: string): boolean {
    return code === "EWPB" || code === "EWPE";
  }
}
